import { SoftwareSystemType } from "../../types/optionalSystems";
import { OptionalSystem } from "../../types/optionalSystems";

/**
 * Get a detailed description for a software system type
 * @param type The type of software system
 * @returns A detailed description of the system
 */
export const getSystemDescription = (type: SoftwareSystemType): string => {
  switch (type) {
    case "manoeuvre":
      return "Basic ship control system.";
    case "intellect":
      return "Shipboard computer system.";
    case "jumpControl":
      return "Jump drive control system.";
    case "evade":
      return "Ship evasion system.";
    case "fireControl":
      return "Weapon targeting system.";
    case "autoRepair":
      return "Automated repair system.";
    case "library":
      return "Information database system.";
    case "advancedFireControl":
      return "Advanced weapon targeting system.";
    case "antiHijack":
      return "Anti-hijacking security system.";
    case "battleNetwork":
      return "Combat coordination system.";
    case "battleSystem":
      return "Advanced combat management system.";
    case "broadSpectrumEw":
      return "Electronic warfare system.";
    case "consciousIntelligence":
      return "Advanced AI system.";
    case "electronicWarfare":
      return "Electronic warfare system.";
    case "launchSolution":
      return "Missile launch control system.";
    case "pointDefence":
      return "Point defense weapon control system.";
    case "screenOptimiser":
      return "Screen system optimization software.";
    case "virtualCrew":
      return "Virtual crew replacement system.";
    case "virtualGunner":
      return "Virtual gunner replacement system.";
    case "interface":
      return "Basic user interface system.";
    case "intelligentInterface":
      return "Advanced user interface system.";
    case "security":
      return "Ship security system.";
    case "intrusion":
      return "Intrusion detection system.";
    case "expert":
      return "Expert system for specialized tasks.";
    case "translator":
      return "Advanced language translation system for communication with alien species.";
    case "database":
      return "Comprehensive information storage and retrieval system.";
    case "agent":
      return "Autonomous AI system that can perform tasks and make decisions independently.";
    default:
      return "Unknown software system type.";
  }
};

/**
 * Get helper text for a software system type
 * @param type The type of software system
 * @returns Helper text explaining the system's requirements and effects
 */
export const getSystemHelperText = (type: SoftwareSystemType): string => {
  switch (type) {
    case "manoeuvre":
      return "Required for all ships. Basic operation requires no bandwidth.";
    case "intellect":
      return "Requires 1 bandwidth per level. Each level improves response time and capability.";
    case "jumpControl":
      return "Required for jump-capable ships. Each level improves jump accuracy.";
    case "evade":
      return "Each level improves evasion capability. Requires Fire Control to be effective.";
    case "fireControl":
      return "Required for weapon operation. Each level improves targeting accuracy.";
    case "autoRepair":
      return "Each level improves repair capability and reduces repair time.";
    case "library":
      return "Each level adds more data and improves search capabilities.";
    case "advancedFireControl":
      return "Requires Fire Control. Each level provides significant accuracy improvements.";
    case "antiHijack":
      return "Each level improves security and reduces hijacking risk.";
    case "battleNetwork":
      return "Each level improves coordination between ship systems.";
    case "battleSystem":
      return "Each level provides advanced combat management capabilities.";
    case "broadSpectrumEw":
      return "Each level improves electronic warfare capabilities.";
    case "consciousIntelligence":
      return "Each level provides more advanced AI capabilities.";
    case "electronicWarfare":
      return "Each level improves electronic warfare capabilities.";
    case "launchSolution":
      return "Each level improves missile launch accuracy.";
    case "pointDefence":
      return "Each level improves point defense effectiveness.";
    case "screenOptimiser":
      return "Each level improves screen system efficiency.";
    case "virtualCrew":
      return "Each level replaces more crew positions.";
    case "virtualGunner":
      return "Each level improves virtual gunner effectiveness.";
    case "interface":
      return "Each level improves user interface capabilities.";
    case "intelligentInterface":
      return "Each level provides more advanced interface features.";
    case "security":
      return "Each level improves security system effectiveness.";
    case "intrusion":
      return "Each level improves intrusion detection capabilities.";
    case "expert":
      return "Each level provides more specialized expertise.";
    case "translator":
      return "Each level improves translation accuracy and language coverage.";
    case "database":
      return "Each level provides more storage and better search capabilities.";
    case "agent":
      return "Each level provides more autonomous decision-making capabilities.";
    default:
      return "No specific requirements or effects known.";
  }
};

// Type definitions for grouped systems
export type GroupedSystem = OptionalSystem & {
  _isGrouped: true;
  _groupedSystems: OptionalSystem[];
  name: string; // Ensure name is always present for grouped systems
  _totalCrewReplacement?: number; // For loading belt systems
};

export type GroupedCapsuleSystem = GroupedSystem & {
  subtype:
    | "internalReEntryCapsule"
    | "internalAssaultCapsule"
    | "internalHighSurvivabilityCapsule"
    | "internalReEntryPod";
};

export type GroupedStableSystem = GroupedSystem & {
  subtype: "internalStable";
};

export type GroupedWorkshopSystem = GroupedSystem & {
  subtype: "internalWorkshop";
};

// Type guard to check if a system is grouped
export const isGroupedSystem = (
  system: OptionalSystem,
): system is GroupedSystem => {
  return (system as any)._isGrouped === true;
};

// Type guard to check if a system is a grouped capsule
export const isGroupedCapsuleSystem = (
  system: OptionalSystem,
): system is GroupedCapsuleSystem => {
  return (
    isGroupedSystem(system) &&
    [
      "internalReEntryCapsule",
      "internalAssaultCapsule",
      "internalHighSurvivabilityCapsule",
      "internalReEntryPod",
    ].includes(system.subtype)
  );
};

// Type guard to check if a system is a grouped stable
export const isGroupedStableSystem = (
  system: OptionalSystem,
): system is GroupedStableSystem => {
  return isGroupedSystem(system) && system.subtype === "internalStable";
};

// Type guard to check if a system is a grouped workshop
export const isGroupedWorkshopSystem = (
  system: OptionalSystem,
): system is GroupedWorkshopSystem => {
  return isGroupedSystem(system) && system.subtype === "internalWorkshop";
};

// Type guard to check if a system is a grouped drone
export const isGroupedDroneSystem = (
  system: OptionalSystem,
): system is GroupedSystem => {
  return isGroupedSystem(system) && system.type === "drone";
};

// Capsule types that should be grouped
export const CAPSULE_TYPES = [
  "internalReEntryCapsule",
  "internalAssaultCapsule",
  "internalHighSurvivabilityCapsule",
  "internalReEntryPod",
] as const;

export type CapsuleType = (typeof CAPSULE_TYPES)[number];

/**
 * Groups capsule systems by subtype and aggregates their properties
 * @param systems Array of optional systems to process
 * @returns Array with grouped capsules and other systems
 */
export const groupCapsuleSystems = (systems: OptionalSystem[]): any[] => {
  // Group capsules by subtype
  const capsuleGroups = CAPSULE_TYPES.map((subtype) => {
    const capsuleSystems = systems.filter((s) => s.subtype === subtype);
    if (capsuleSystems.length === 0) return null;

    const totalTonnage = capsuleSystems.reduce(
      (sum, s) => sum + (typeof s.tonnage === "number" ? s.tonnage : 0),
      0,
    );
    const totalCost = capsuleSystems.reduce((sum, s) => sum + (s.cost || 0), 0);
    const firstSystem = capsuleSystems[0];

    return {
      ...firstSystem,
      id: `grouped_${subtype}`,
      tonnage: totalTonnage,
      cost: totalCost,
      _groupedSystems: capsuleSystems,
      _isGrouped: true,
    } as GroupedCapsuleSystem;
  }).filter(Boolean);

  // Get non-capsule systems
  const nonCapsuleSystems = systems.filter(
    (s) => !CAPSULE_TYPES.includes(s.subtype as CapsuleType),
  );

  // Combine grouped capsules with other systems
  return [...capsuleGroups, ...nonCapsuleSystems];
};

/**
 * Groups Stable systems and aggregates their properties
 * @param systems Array of optional systems to process
 * @returns Array with grouped stables and other systems
 */
export const groupStableSystems = (systems: OptionalSystem[]): any[] => {
  // Group stables by subtype
  const stableSystems = systems.filter((s) => s.subtype === "internalStable");
  const otherSystems = systems.filter((s) => s.subtype !== "internalStable");

  if (stableSystems.length === 0) {
    return otherSystems;
  }

  if (stableSystems.length === 1) {
    return [...otherSystems, ...stableSystems];
  }

  // Group multiple stables
  const totalTonnage = stableSystems.reduce(
    (sum, s) => sum + (typeof s.tonnage === "number" ? s.tonnage : 0),
    0,
  );
  const totalCost = stableSystems.reduce((sum, s) => sum + (s.cost || 0), 0);
  const firstSystem = stableSystems[0];

  const groupedStable = {
    ...firstSystem,
    id: `grouped_internalStable`,
    tonnage: totalTonnage,
    cost: totalCost,
    _groupedSystems: stableSystems,
    _isGrouped: true,
  } as GroupedStableSystem;

  return [...otherSystems, groupedStable];
};

/**
 * Groups Workshop systems and aggregates their properties
 * @param systems Array of optional systems to process
 * @returns Array with grouped workshops and other systems
 */
export const groupWorkshopSystems = (systems: OptionalSystem[]): any[] => {
  // Group workshops by subtype
  const workshopSystems = systems.filter(
    (s) => s.subtype === "internalWorkshop",
  );
  const otherSystems = systems.filter((s) => s.subtype !== "internalWorkshop");

  if (workshopSystems.length === 0) {
    return otherSystems;
  }

  if (workshopSystems.length === 1) {
    return [...otherSystems, ...workshopSystems];
  }

  // Group multiple workshops
  const totalTonnage = workshopSystems.reduce(
    (sum, s) => sum + (typeof s.tonnage === "number" ? s.tonnage : 0),
    0,
  );
  const totalCost = workshopSystems.reduce((sum, s) => sum + (s.cost || 0), 0);
  const firstSystem = workshopSystems[0];

  const groupedWorkshop = {
    ...firstSystem,
    id: `grouped_internalWorkshop`,
    tonnage: totalTonnage,
    cost: totalCost,
    _groupedSystems: workshopSystems,
    _isGrouped: true,
  } as GroupedWorkshopSystem;

  return [...otherSystems, groupedWorkshop];
};

/**
 * Groups Drone systems by subtype and aggregates their properties
 * @param systems Array of optional systems to process
 * @returns Array with grouped drones and other systems
 */
export const groupDroneSystems = (systems: OptionalSystem[]): any[] => {
  // Define drone subtypes that should be grouped
  const DRONE_TYPES = ["mining", "probe", "advancedProbe", "repair"];

  // Group drones by subtype
  const droneGroups = DRONE_TYPES.map((subtype) => {
    const droneSystems = systems.filter((s) => s.subtype === subtype);
    if (droneSystems.length === 0) return null;

    if (droneSystems.length === 1) {
      return droneSystems[0];
    }

    // Group multiple drones of the same type
    const totalTonnage = droneSystems.reduce(
      (sum, s) => sum + (typeof s.tonnage === "number" ? s.tonnage : 0),
      0,
    );
    const totalCost = droneSystems.reduce((sum, s) => sum + (s.cost || 0), 0);
    const totalPower = droneSystems.reduce((sum, s) => sum + (s.power || 0), 0);
    const firstSystem = droneSystems[0];

    return {
      ...firstSystem,
      id: `grouped_${subtype}`,
      tonnage: totalTonnage,
      cost: totalCost,
      power: totalPower,
      _groupedSystems: droneSystems,
      _isGrouped: true,
    } as GroupedSystem;
  }).filter(Boolean);

  // Get non-drone systems
  const nonDroneSystems = systems.filter(
    (s) => !DRONE_TYPES.includes(s.subtype as any),
  );

  // Combine grouped drones with other systems
  return [...droneGroups, ...nonDroneSystems];
};

/**
 * Groups Loading Belt systems by subtype and aggregates their properties
 * @param systems Array of optional systems to process
 * @returns Array with grouped loading belts and other systems
 */
export const groupLoadingBeltSystems = (systems: OptionalSystem[]): any[] => {
  // Define loading belt subtypes that should be grouped
  const LOADING_BELT_TYPES = ["loadingBelt7", "loadingBelt12"];

  // Group loading belts by subtype
  const loadingBeltGroups = LOADING_BELT_TYPES.map((subtype) => {
    const loadingBeltSystems = systems.filter((s) => s.subtype === subtype);
    if (loadingBeltSystems.length === 0) return null;

    if (loadingBeltSystems.length === 1) {
      return loadingBeltSystems[0];
    }

    // Group multiple loading belts of the same type
    const totalTonnage = loadingBeltSystems.reduce(
      (sum, s) => sum + (typeof s.tonnage === "number" ? s.tonnage : 0),
      0,
    );
    const totalCost = loadingBeltSystems.reduce(
      (sum, s) => sum + (s.cost || 0),
      0,
    );
    const totalPower = loadingBeltSystems.reduce(
      (sum, s) => sum + (s.power || 0),
      0,
    );
    const crewReplacement =
      (loadingBeltSystems[0].properties as any)?.crewReplacement || 25;
    const totalCrewReplacement = crewReplacement * loadingBeltSystems.length;
    const firstSystem = loadingBeltSystems[0];

    return {
      ...firstSystem,
      id: `grouped_${subtype}`,
      tonnage: totalTonnage,
      cost: totalCost,
      power: totalPower,
      _groupedSystems: loadingBeltSystems,
      _isGrouped: true,
      _totalCrewReplacement: totalCrewReplacement,
    } as GroupedSystem;
  }).filter(Boolean);

  // Get non-loading belt systems
  const nonLoadingBeltSystems = systems.filter(
    (s) => !LOADING_BELT_TYPES.includes(s.subtype as any),
  );

  // Combine grouped loading belts with other systems
  return [...loadingBeltGroups, ...nonLoadingBeltSystems];
};

/**
 * Groups capsule, stable, workshop, drone, and loading belt systems
 * @param systems Array of optional systems to process
 * @returns Array with grouped systems
 */
export const groupSystems = (systems: OptionalSystem[]): any[] => {
  // First group capsules
  const capsuleGrouped = groupCapsuleSystems(systems);

  // Then group stables from the remaining systems
  const nonCapsuleSystems = capsuleGrouped.filter(
    (s) => !isGroupedCapsuleSystem(s),
  );
  const stableGrouped = groupStableSystems(nonCapsuleSystems);

  // Then group workshops from the remaining systems
  const nonStableSystems = stableGrouped.filter(
    (s) => !isGroupedStableSystem(s),
  );
  const workshopGrouped = groupWorkshopSystems(nonStableSystems);

  // Then group drones from the remaining systems
  const nonWorkshopSystems = workshopGrouped.filter(
    (s) => !isGroupedWorkshopSystem(s),
  );
  const droneGrouped = groupDroneSystems(nonWorkshopSystems);

  // Then group loading belts from the remaining systems
  const nonDroneSystems = droneGrouped.filter(
    (s) => !isGroupedSystem(s) || s.type !== "drone",
  );
  const loadingBeltGrouped = groupLoadingBeltSystems(nonDroneSystems);

  // Combine grouped capsules with grouped stables, grouped workshops, grouped drones, grouped loading belts, and other systems
  const groupedCapsules = capsuleGrouped.filter((s) =>
    isGroupedCapsuleSystem(s),
  );
  const groupedStables = stableGrouped.filter((s) => isGroupedStableSystem(s));
  const groupedWorkshops = workshopGrouped.filter((s) =>
    isGroupedWorkshopSystem(s),
  );
  const groupedDrones = droneGrouped.filter(
    (s) => isGroupedSystem(s) && s.type === "drone",
  );
  const groupedLoadingBelts = loadingBeltGrouped.filter(
    (s) =>
      isGroupedSystem(s) &&
      s.type === "cargo" &&
      (s.subtype === "loadingBelt7" || s.subtype === "loadingBelt12"),
  );
  const otherSystems = loadingBeltGrouped.filter(
    (s) =>
      !isGroupedSystem(s) ||
      s.type !== "cargo" ||
      (s.subtype !== "loadingBelt7" && s.subtype !== "loadingBelt12"),
  );

  return [
    ...groupedCapsules,
    ...groupedStables,
    ...groupedWorkshops,
    ...groupedDrones,
    ...groupedLoadingBelts,
    ...otherSystems,
  ];
};

/**
 * Removes all systems in a grouped system
 * @param system The grouped system to remove
 * @param removeFunction Function to remove individual systems
 */
export const removeGroupedSystem = (
  system: GroupedSystem,
  removeFunction: (system: OptionalSystem) => void,
): void => {
  if (system._isGrouped && system._groupedSystems) {
    // Remove all systems in the group
    system._groupedSystems.forEach((groupedSystem) => {
      removeFunction(groupedSystem);
    });
  } else {
    // Fallback to removing the single system
    removeFunction(system);
  }
};
